import saveAs from 'file-saver';
import { format } from 'date-fns';
import { numberToCurrencyString } from 'further-ui/utils';
import { AccountType } from 'further-types/investor';
import { ExitDetailsFormValues } from '../ExitDetailsForm';
import type { ShareConsideration } from '../types';
import { round } from 'lodash';

const greyOutColumn = {
  fill: {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'e6e6e6' },
    bgColor: { argb: 'FF0000FF' },
  },
};

const noOfSharesFormat = {
  numFmt: '#,##0',
};

type DownloadShareConsiderationTemplateParams = {
  exitDetails: ExitDetailsFormValues;
  shareConsiderations: Array<ShareConsideration>;
  setAllSharesToZero?: boolean;
  filename?: string;
  isBeingEdited?: boolean;
};

const getInvestorFirstName = (
  investor: ShareConsideration['investor'],
): string => {
  if (!investor) return '';
  if (investor.accountType === AccountType.LegalEntity) {
    return '';
  }
  return investor.firstName ?? '';
};

const getInvestorLastName = (
  investor: ShareConsideration['investor'],
): string => {
  if (!investor) return '';
  if (investor.accountType === AccountType.LegalEntity) {
    return investor.registeredName ?? '';
  }
  return investor.lastName ?? '';
};

export const downloadShareConsiderationTemplate = async ({
  exitDetails,
  shareConsiderations,
  setAllSharesToZero = false,
  filename,
  isBeingEdited = false,
}: DownloadShareConsiderationTemplateParams) => {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(`Share-Exit-${Date.now()}`);

  // Get company name and share class
  const firstConsideration = shareConsiderations[0];
  const shareClassName = firstConsideration?.shareClass || '';
  const companyName = shareClassName || 'Company';
  const considerationSharePrice = Number(
    exitDetails?.shareConsiderationValuePerShare || 0,
  );
  const exitDate = exitDetails?.exitDate
    ? format(new Date(exitDetails.exitDate), 'dd/MM/yyyy')
    : 'DD/MM/YYYY';

  // Row 1: Note to user
  worksheet.getCell('A1').value =
    'NOTE TO USER: COMPLETE GREY FIELDS ONLY (PLEASE DO NOT AMEND OTHER FIELDS)';
  worksheet.getRow(1).getCell(1).font = { bold: true };
  worksheet.getRow(1).getCell(1).fill = {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'b2d1a9' },
  };
  worksheet.mergeCells('A1', 'J1');

  worksheet.getCell('K1').value = 'System IDs (do not amend - used by system)';
  worksheet.getRow(1).getCell(11).font = { bold: true };
  worksheet.getRow(1).getCell(11).fill = {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'a4c2f4' },
  };
  worksheet.mergeCells('K1', 'M1');

  // Row 2: Note about values
  worksheet.getCell('A2').value =
    'Note: Each grey cell must be a value not a formula.';
  worksheet.getRow(2).getCell(1).font = { bold: true };
  worksheet.mergeCells('A2', 'C2');

  // Row 4: Company name
  worksheet.getCell('A4').value = 'Company';
  worksheet.getCell('B4').value = `${companyName} (${shareClassName})`;

  // Row 5: Consideration share price
  worksheet.getCell('A5').value = 'Consideration share price';
  worksheet.getCell('B5').value = numberToCurrencyString(
    considerationSharePrice,
    { unlimitedDp: true },
  );

  // Row 6: Sale date
  worksheet.getCell('A6').value = 'Sale date';
  worksheet.getCell('B6').value = exitDate;

  // Row 7: Empty
  worksheet.getRow(7);

  // Row 8: Column headers
  const shareConsiderationColumns = [
    'Tranche',
    'First name',
    'Last/corporate name',
    'Subscription date',
    'Allocation date',
    'Shares held',
    'Exit shares sold',
    'New shares allocated',
    'Share value',
    'External subscription ID',
    'System subscription ID',
    'Company ID',
    'Shareholding ID',
  ];
  worksheet.getRow(8).values = shareConsiderationColumns;
  worksheet.getRow(8).font = { bold: true };

  // Set column widths
  worksheet.columns = [
    { width: 20 }, // Tranche
    { width: 20 }, // First name
    { width: 25 }, // Last/corporate name
    { width: 20 }, // Subscription date
    { width: 20 }, // Allocation date
    { width: 18 }, // Shares held
    { width: 18 }, // Exit shares sold
    { width: 20 }, // New shares allocated (editable)
    { width: 15 }, // Share value
    { width: 25 }, // External subscription ID
    { width: 25 }, // System subscription ID
    { width: 20 }, // Company ID
    { width: 25 }, // Shareholding ID
  ];

  // Data rows (starting at row 9)
  shareConsiderations.forEach((sc, i) => {
    const rowNum = i + 9;
    const firstName = getInvestorFirstName(sc.investor);
    const lastName = getInvestorLastName(sc.investor);
    const subscriptionDate = sc.investmentDate
      ? format(new Date(sc.investmentDate), 'dd/MM/yyyy')
      : '';
    const allocationDate = sc.allocationDate
      ? format(new Date(sc.allocationDate), 'dd/MM/yyyy')
      : '';
    // Calculate total share value: new shares allocated * share value per share, rounded to 2dp
    const newSharesAllocated = setAllSharesToZero
      ? 0
      : sc.newSharesAllocated ?? 0;
    const shareValuePerShare = sc.shareConsiderationValuePerShare ?? 0;
    const totalShareValue = newSharesAllocated * shareValuePerShare;
    const roundedShareValue = round(totalShareValue, 2);
    const shareValue =
      newSharesAllocated > 0 && shareValuePerShare > 0
        ? numberToCurrencyString(roundedShareValue, {})
        : '';

    // "Exit shares sold" (column 7) should be grey (editable) only for original exits, not amendments
    // "New shares allocated" (column 8) should always be grey (editable)
    const row = worksheet.getRow(rowNum);
    row.getCell(1).value = sc.tranche?.fundName || ''; // Tranche
    row.getCell(2).value = firstName; // First name
    row.getCell(3).value = lastName; // Last/corporate name
    row.getCell(4).value = subscriptionDate; // Subscription date
    row.getCell(5).value = allocationDate; // Allocation date
    row.getCell(6).value = sc.noOfShares ?? 0; // Shares held
    row.getCell(6).numFmt = noOfSharesFormat.numFmt;
    row.getCell(7).value = sc.sharesAllocatedForSale; // Exit shares sold
    // Only grey out (make editable) if it's NOT being edited (i.e., original exit)
    if (!isBeingEdited) {
      row.getCell(7).fill = greyOutColumn.fill;
    }
    row.getCell(7).numFmt = noOfSharesFormat.numFmt;
    row.getCell(8).value = setAllSharesToZero ? 0 : sc.newSharesAllocated ?? 0; // New shares allocated (editable - grey)
    row.getCell(8).fill = greyOutColumn.fill;
    row.getCell(8).numFmt = '#,##0.00'; // Display number with 2 decimal places (always shows .00 if no decimals)
    row.getCell(9).value = shareValue; // Share value
    row.getCell(10).value = sc.externalInvestmentId || ''; // External subscription ID
    row.getCell(11).value = sc.shareholdingId; // System investment ID (this is actually the investment ID, not shareholdingId)
    row.getCell(12).value = exitDetails?.shareConsiderationCompanyId || ''; // Company ID
    row.getCell(13).value = sc.shareholdingId; // Shareholding ID (actual shareholdingId)
  });

  workbook.xlsx.writeBuffer().then(function (buffer) {
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      filename || `Share-Exit-amendments-file-${Date.now()}.xlsx`,
    );
  });
};
